// 職責：門店自定義渠道（例：浸會 801「燒味」）的資料格式、解析與組號分配 —— 純函式

/*
 * ── 這件事是什麼（Peter 2026-09-25）─────────────────────────────────────────
 * 每個自定義渠道就是一組 `merchant_period.menu_group`，組號固定在 **30–39** 這一段。
 * 本後台「供應時段」每個渠道一個分頁（排在「自助售賣」之後），在那裡開時段、掛餐品；
 * kiosk 設定頁勾選之後，開始頁就會多出該渠道的按鈕（燒味堂食／燒味外賣）。
 * 後端取餐牌是 `(merchant_period.menu_group ?? 0) == gid`，沒有白名單，所以 webapi 不需要改。
 *
 * ── 為什麼是 30–39 ────────────────────────────────────────────────────────
 * 0–12、15、16、20、21 已經有用途（12 = kiosk 外賣配件，見 kiosk-android
 * `src/takeaway-extras.ts`）。自定義渠道要預留多個，13 之後沒有足夠的連續空號；
 * 另開一段專用段，將來再加外賣平台亦不會撞號。
 *
 * ── 資料格式（🚨 三處必須一字不差）──────────────────────────────────────────
 *   kiosk-android `src/custom-menu-group.ts`
 *   shop-manager  `src/domain/rules/customMenuGroup.ts`（本檔）
 *   scpos         `packages/vue/src/utils/custom-menu-group.ts`
 *
 * 每個渠道一行 `merchant_config`：
 *   merchant_id  = 本店 mid
 *   config_name  = 'custom-menu-group'
 *   config_code  = 組號字串，只接受 '30'…'39'
 *   config_value = JSON，例 {"name":"燒味","name_en":"Roast Meat"}
 *
 * 解析規則：
 *   ① 只認 config_name === 'custom-menu-group' 而且 merchant_id === 本店 mid
 *      （本後台的 `useConfigEntries` 已經先按 mid 過濾，所以這裡收的是已過濾的列）。
 *   ② config_code 去空白後必須是 30–39 的純數字字串，否則略過。
 *   ③ config_value 解析得到 JSON object → 取 name / name_en；
 *      解析不到 → 整個值當作中文名，英文名留空。
 *   ④ 同一組號重複多行 → 取 id 最小的那一行。
 *   ⑤ 輸出按組號由小至大排列。
 *   ⑥ 顯示：中文名；空 →「自定義 30」。（英文名只給 kiosk 英文介面用，本後台不顯示。）
 */

/** `merchant_config.config_name` —— 所有自定義渠道共用，靠 config_code 分組號 */
export const CUSTOM_MENU_GROUP_CONFIG_NAME = 'custom-menu-group'
export const CUSTOM_MENU_GROUP_MIN = 30
export const CUSTOM_MENU_GROUP_MAX = 39

/**
 * 名稱長度上限。
 * ★ 不是資料庫的限制（config_value 是 text），是 **kiosk 按鈕放得下**：開始頁四個按鈕排成
 *   2×2，每格約 438px、字高 46px —— 「{名稱}堂食」中文大約 8 個字已經頂到邊。
 *   英文會斷行，所以寬鬆一些。
 */
export const CUSTOM_MENU_GROUP_NAME_MAX = 6
export const CUSTOM_MENU_GROUP_NAME_EN_MAX = 20

/** 30–39 的組號 */
export type CustomMenuGroupNo = 30 | 31 | 32 | 33 | 34 | 35 | 36 | 37 | 38 | 39

export interface CustomMenuGroup {
  /** `merchant_period.menu_group`，30–39 */
  group: CustomMenuGroupNo
  /** 中文名（可以是空字串，顯示時補回預設名） */
  name: string
  /** 英文名（可以是空字串，kiosk 英文介面會退回中文名） */
  nameEn: string
  /** 該列 `merchant_config.id` —— 改名、刪除時要用 */
  configId: number
}

/** 本後台的 `ConfigEntry` 形狀（已按 mid 過濾），只取本檔需要的欄位 */
export interface CustomMenuGroupConfigRow {
  id: number
  code: string
  name: string
  value: string
}

export function isCustomMenuGroup(value: unknown): value is CustomMenuGroupNo {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= CUSTOM_MENU_GROUP_MIN &&
    value <= CUSTOM_MENU_GROUP_MAX
  )
}

/** config_code → 組號；不合法回 null。只接受純數字字串，「3a」「30.5」一律不收。 */
function groupOfCode(code: string): CustomMenuGroupNo | null {
  const raw = code.trim()
  if (!/^\d+$/.test(raw)) return null
  const n = Number(raw)
  return isCustomMenuGroup(n) ? n : null
}

function namesOfValue(value: string): { name: string; nameEn: string } {
  try {
    const obj: unknown = JSON.parse(value)
    if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
      const rec = obj as Record<string, unknown>
      return {
        name: String(rec.name ?? '').trim(),
        nameEn: String(rec.name_en ?? '').trim(),
      }
    }
  } catch {
    // 不是 JSON —— 落到下面，整個值當作中文名
  }
  return { name: value.trim(), nameEn: '' }
}

/** 由本店的 `merchant_config` 列整理出自定義渠道清單。 */
export function parseCustomMenuGroups(
  rows: readonly CustomMenuGroupConfigRow[],
): CustomMenuGroup[] {
  const best = new Map<CustomMenuGroupNo, CustomMenuGroup>()
  for (const row of rows) {
    if (row.name !== CUSTOM_MENU_GROUP_CONFIG_NAME) continue
    const group = groupOfCode(row.code)
    if (group === null) continue
    const prev = best.get(group)
    if (prev !== undefined && prev.configId <= row.id) continue
    best.set(group, { group, ...namesOfValue(row.value), configId: row.id })
  }
  return [...best.values()].sort((a, b) => a.group - b.group)
}

/** 顯示名稱（本後台只用中文名）。 */
export function customMenuGroupLabel(
  ch: Pick<CustomMenuGroup, 'group' | 'name'> | undefined,
  group: number,
): string {
  const name = ch?.name.trim() ?? ''
  return name !== '' ? name : `自定義 ${String(group)}`
}

/**
 * 新開一個渠道用哪個組號：30–39 之中第一個**完全沒有人用過**的號。
 *
 * 🚨 「沒有人用過」要連時段一起看，不只看 merchant_config：渠道被刪除之後，
 *    若那組號底下仍然有時段（例如用 SQL 刪了設定列），再開新渠道拿回同一個號，
 *    舊時段就會即時出現在新渠道的餐牌 —— 所以 `usedByPeriods` 一定要傳。
 *
 * 全部用完 → null（畫面要擋住「新增」並說明原因）。
 */
export function nextFreeCustomMenuGroup(
  existing: readonly { group: number }[],
  usedByPeriods: Iterable<number | null>,
): CustomMenuGroupNo | null {
  const taken = new Set<number>(existing.map((c) => c.group))
  for (const g of usedByPeriods) if (g !== null) taken.add(g)
  for (let g = CUSTOM_MENU_GROUP_MIN; g <= CUSTOM_MENU_GROUP_MAX; g++) {
    if (!taken.has(g) && isCustomMenuGroup(g)) return g
  }
  return null
}

/** 寫入 `config_value` 的 JSON 字串（鍵名與 kiosk／scpos 讀取的一致）。 */
export function customMenuGroupConfigValue(name: string, nameEn: string): string {
  return JSON.stringify({ name: name.trim(), name_en: nameEn.trim() })
}

/** 名稱檢查。回傳錯誤訊息；null = 合格。 */
export function validateCustomMenuGroupNames(
  name: string,
  nameEn: string,
  others: readonly { group: number; name: string }[],
  selfGroup: number | null,
): string | null {
  const zh = name.trim()
  const en = nameEn.trim()
  if (zh === '') return '請輸入中文名稱'
  if ([...zh].length > CUSTOM_MENU_GROUP_NAME_MAX) {
    return `中文名稱最多 ${String(CUSTOM_MENU_GROUP_NAME_MAX)} 個字（自助機按鈕放不下）`
  }
  if (en.length > CUSTOM_MENU_GROUP_NAME_EN_MAX) {
    return `英文名稱最多 ${String(CUSTOM_MENU_GROUP_NAME_EN_MAX)} 個字元（自助機按鈕放不下）`
  }
  if (others.some((c) => c.group !== selfGroup && c.name.trim() === zh)) {
    return `已經有一個叫「${zh}」的渠道`
  }
  return null
}
